import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { FileText, Search, RefreshCw, User, Clock, ArrowRight } from 'lucide-react';

const ACTION_CONFIG = {
  create: { label: 'Crear', color: '#10B981', icon: '+' },
  update: { label: 'Editar', color: '#3B82F6', icon: '✎' },
  delete: { label: 'Eliminar', color: '#EF4444', icon: '✕' },
  login: { label: 'Login', color: '#8B5CF6', icon: '→' },
  logout: { label: 'Logout', color: '#64748B', icon: '←' },
  export: { label: 'Exportar', color: '#06B6D4', icon: '↓' },
  import: { label: 'Importar', color: '#F59E0B', icon: '↑' },
  approve: { label: 'Aprobar', color: '#10B981', icon: '✓' },
  revoke: { label: 'Revocar', color: '#EF4444', icon: '⊘' },
  assign: { label: 'Asignar', color: '#D4A017', icon: '→' },
};

const ENTITY_LABELS = {
  project: 'Proyecto', task: 'Tarea', milestone: 'Hito', equipment: 'Equipo',
  photo: 'Foto', incident: 'Incidencia', checklist: 'Checklist', document: 'Documento',
  certificate: 'Certificado', user: 'Usuario', crew: 'Cuadrilla', budget: 'Presupuesto',
  inventory: 'Inventario', expense: 'Gasto',
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: '', action: '', project_id: '', limit: 100 });
  const [projects, setProjects] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {});
    loadData();
  }, []);

  const loadData = async (f) => {
    setLoading(true);
    const params = f || filters;
    try {
      const [l, s] = await Promise.all([
        api.get('/audit', { params }),
        api.get('/audit/stats', { params: { project_id: params.project_id, days: 30 } }),
      ]);
      setLogs(l.data);
      setStats(s.data);
    } catch (e) {} finally { setLoading(false); }
  };

  const applyFilter = (key, value) => {
    const f = { ...filters, [key]: value };
    setFilters(f);
    loadData(f);
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Ahora mismo';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    return d.toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const renderChanges = (changes) => {
    if (!changes) return null;
    return (
      <div className="aud-changes">
        {Object.entries(changes).filter(([k]) => !['updated_at', 'id'].includes(k)).slice(0, 8).map(([key, val]) => (
          <div key={key} className="aud-change-row">
            <span className="aud-change-key">{key}:</span>
            {typeof val === 'object' && val?.old !== undefined ? (
              <><span className="aud-change-old">{String(val.old)}</span><ArrowRight size={10} /><span className="aud-change-new">{String(val.new)}</span></>
            ) : (
              <span className="aud-change-new">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="audit-page">
      <div className="page-header">
        <div><h1><FileText size={24} /> Log de Auditoría</h1><p className="subtitle">Historial de cambios del sistema</p></div>
        <button className="btn btn-secondary" onClick={() => loadData()}><RefreshCw size={14} /> Actualizar</button>
      </div>

      {/* Stats */}
      <div className="aud-stats">
        <div className="aud-stat"><span className="aud-stat-val">{stats.total_events || 0}</span><span className="aud-stat-lbl">Eventos (30d)</span></div>
        <div className="aud-stat"><span className="aud-stat-val">{stats.active_users || 0}</span><span className="aud-stat-lbl">Usuarios activos</span></div>
        <div className="aud-stat"><span className="aud-stat-val" style={{ color: '#10B981' }}>{stats.creates || 0}</span><span className="aud-stat-lbl">Creaciones</span></div>
        <div className="aud-stat"><span className="aud-stat-val" style={{ color: '#3B82F6' }}>{stats.updates || 0}</span><span className="aud-stat-lbl">Ediciones</span></div>
        <div className="aud-stat"><span className="aud-stat-val" style={{ color: '#EF4444' }}>{stats.deletes || 0}</span><span className="aud-stat-lbl">Eliminaciones</span></div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select className="filter-select" value={filters.project_id} onChange={e => applyFilter('project_id', e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <select className="filter-select" value={filters.entity_type} onChange={e => applyFilter('entity_type', e.target.value)}>
          <option value="">Todas las entidades</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="filter-select" value={filters.action} onChange={e => applyFilter('action', e.target.value)}>
          <option value="">Todas las acciones</option>
          {Object.entries(ACTION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Timeline */}
      <div className="aud-timeline">
        {logs.length === 0 && !loading && <div className="empty-state"><FileText size={48} /><h3>Sin eventos</h3></div>}
        {logs.map(log => {
          const ac = ACTION_CONFIG[log.action] || { label: log.action, color: '#64748B', icon: '•' };
          const isExpanded = expandedId === log.id;
          return (
            <div key={log.id} className="aud-entry" onClick={() => setExpandedId(isExpanded ? null : log.id)}>
              <div className="aud-entry-dot" style={{ background: ac.color }}>{ac.icon}</div>
              <div className="aud-entry-content">
                <div className="aud-entry-header">
                  <span className="aud-entry-action" style={{ color: ac.color }}>{ac.label}</span>
                  <span className="aud-entry-entity">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                  {log.entity_name && <span className="aud-entry-name">{log.entity_name}</span>}
                  {log.project_code && <span className="aud-entry-proj">{log.project_code}</span>}
                </div>
                <div className="aud-entry-meta">
                  <span><User size={10} /> {log.user_name || log.user_email || 'Sistema'}</span>
                  <span><Clock size={10} /> {formatTime(log.created_at)}</span>
                </div>
                {isExpanded && log.changes && renderChanges(log.changes)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
